import React, { useState, useEffect } from 'react';
import { SectionDivider } from '../components/Layout';

export default function NewsPage() {
  const [newsData, setNewsData] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [instagramPosts, setInstagramPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  // カテゴリ別の画像パス設定
  const getCategoryImagePath = (category, isSelected) => {
    const suffix = isSelected ? '1' : '2';
    
    if (category === 'all') {
      return `/images/news/news-all${suffix}.png`;
    } else if (category === 'お知らせ') {
      return `/images/news/news-notice${suffix}.png`;
    } else if (category === '受賞') {
      return `/images/news/news-award${suffix}.png`;
    } else if (category === 'プレスリリース') {
      return `/images/news/news-press${suffix}.png`;
    } else if (category === 'イベント') {
      return `/images/news/news-event${suffix}.png`;
    }
    return `/images/news/news-all${suffix}.png`;
  };

  // カテゴリ別の背景色設定
  const getCategoryBgColor = (category) => {
    const categoryValue = Array.isArray(category) ? category[0] : category;
    
    if (categoryValue === 'お知らせ') {
      return '#dbeafe';
    } else if (categoryValue === '受賞') {
      return '#fefce8';
    } else if (categoryValue === 'プレスリリース') {
      return '#f0fdf4';
    } else if (categoryValue === 'イベント') {
      return '#faf5ff';
    }
    return '#ffffff'; // デフォルト
  };

  // タイトルの内容によって背景画像を決定
  const getBackgroundImage = (title) => {
    if (title.includes('1位') || title.includes('第1位') || title.includes('１位') || title.includes('第１位')) {
      return '/images/about/jusho1.jpg';
    } else if (title.includes('2位') || title.includes('第2位') || title.includes('２位') || title.includes('第２位')) {
      return '/images/about/jusho2.jpg';
    } else if (title.includes('3位') || title.includes('第3位') || title.includes('３位') || title.includes('第３位')) {
      return '/images/about/jusho3.jpg';
    } else {
      return '/images/about/jusho4.jpg';
    }
  };

  // microCMSからニュースデータを取得
  useEffect(() => {
    const fetchNewsData = async () => {
      try {
        const response = await fetch(
          `https://${process.env.REACT_APP_MICROCMS_SERVICE_DOMAIN}.microcms.io/api/v1/news?filters=display_location[contains]お知らせ一覧&limit=50&orders=-date`,
          {
            headers: {
              'X-MICROCMS-API-KEY': process.env.REACT_APP_MICROCMS_API_KEY,
            },
          }
        );
        const data = await response.json();
        
        if (data.contents) {
          // isActiveが「表示」のデータのみフィルタリング（配列の最初の要素を取得）
          const displayData = data.contents.filter(item => item.isActive[0] === '表示');
          setNewsData(displayData);
        }
      } catch (error) {
        console.log('Announcement could not be loaded');
      } finally {
        setLoading(false);
      }
    };

    fetchNewsData();
  }, []);

  // microCMSから受賞歴データを取得
  useEffect(() => {
    const fetchAchievements = async () => {
      try {
        const response = await fetch(
          `https://${process.env.REACT_APP_MICROCMS_SERVICE_DOMAIN}.microcms.io/api/v1/news?filters=display_location[contains]農園についてページ&limit=2&orders=-date`,
          {
            headers: {
              'X-MICROCMS-API-KEY': process.env.REACT_APP_MICROCMS_API_KEY,
            },
          }
        );
        const data = await response.json();
        
        if (data.contents) {
          setAchievements(data.contents);
        }
      } catch (error) {
        console.log('実績データを取得できませんでした');
      }
    };

    fetchAchievements();
  }, []);

  // microCMSからInstagramデータを取得
  useEffect(() => {
    const fetchInstagramData = async () => {
      try {
        const response = await fetch(
          `https://${process.env.REACT_APP_MICROCMS_SERVICE_DOMAIN}.microcms.io/api/v1/instagram_posts?limit=6&orders=-createdAt`,
          {
            headers: {
              'X-MICROCMS-API-KEY': process.env.REACT_APP_MICROCMS_API_KEY,
            },
          }
        );
        const data = await response.json();
        
        if (data.contents) {
          setInstagramPosts(data.contents);
        }
      } catch (error) {
        console.log('Instagram data could not be loaded');
      }
    };

    fetchInstagramData();
  }, []);

  // カテゴリフィルタリング
  const filteredNews = selectedCategory === 'all' 
    ? newsData 
    : newsData.filter(item => {
        // categoryが配列の場合は最初の要素を取得
        const categoryValue = Array.isArray(item.category) ? item.category[0] : item.category;
        return categoryValue === selectedCategory;
      });

  // カテゴリ一覧（固定値）
  const categories = ['all', 'お知らせ', '受賞', 'プレスリリース', 'イベント'];

  // 日付フォーマット（年月のみ）
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${date.getFullYear()}年${date.getMonth() + 1}月`;
  };

  return (
    <div className="min-h-screen relative">
      
      {/* ヒーローセクション */}
      <section
        className="h-80 md:h-screen flex items-center justify-center relative pt-8 md:pt-16"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3)), url("/images/about/hyosyo.jpg")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* PCバージョン背景画像 */}
        <div
          className="hidden md:block absolute inset-0 z-0"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3)), url("/images/about/hyosyo.jpg")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        ></div>
        
        <div className="text-center relative z-10">
          <h1 className="font-bold text-white leading-tight mb-6" style={{
            fontSize: window.innerWidth >= 768 ? '56px' : '24px',
            textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
          }}>
            <span style={{color: '#fed7aa'}}>最新情報・受賞歴など</span>
          </h1>
        </div>
      </section>

      {/* SVG区切り1 */}
      <SectionDivider title="news" />

      <div className="tateishi-content ml-0 md:ml-40">
        <div className="max-w-6xl mx-auto">

          {/* カテゴリフィルターセクション */}
          <section className="tateishi-section">
            <div className="-mx-4 md:px-6">
              <div className="text-center mb-4 md:mb-12">
                <h2 className="heading-1 mb-4" style={{color: '#8b4513'}}>
                  カテゴリから選ぶ
                </h2>
              </div>
              
              {/* カテゴリボタン */}
              <div className="max-w-sm mx-auto md:max-w-none md:flex md:flex-wrap md:justify-center md:gap-4 mb-2 space-y-2 md:space-y-0">
                {/* スマホ版：カスタムレイアウト */}
                <div className="md:hidden">
                  {/* 1行目：すべて（中央） */}
                  <div className="flex justify-center mb-2">
                    <button
                      onClick={() => setSelectedCategory('all')}
                      className="transition-opacity hover:opacity-80"
                    >
                      <img 
                        src={getCategoryImagePath('all', selectedCategory === 'all')}
                        alt="すべて"
                        className="w-24 h-7"
                      />
                    </button>
                  </div>
                  
                  {/* 2行目：お知らせ、受賞 */}
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <button
                      onClick={() => setSelectedCategory('お知らせ')}
                      className="transition-opacity hover:opacity-80"
                    >
                      <img 
                        src={getCategoryImagePath('お知らせ', selectedCategory === 'お知らせ')}
                        alt="お知らせ"
                        className="w-24 h-7 mx-auto"
                      />
                    </button>
                    <button
                      onClick={() => setSelectedCategory('受賞')}
                      className="transition-opacity hover:opacity-80"
                    >
                      <img 
                        src={getCategoryImagePath('受賞', selectedCategory === '受賞')}
                        alt="受賞"
                        className="w-24 h-7 mx-auto"
                      />
                    </button>
                  </div>
                  
                  {/* 3行目：プレスリリース、イベント */}
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setSelectedCategory('プレスリリース')}
                      className="transition-opacity hover:opacity-80"
                    >
                      <img 
                        src={getCategoryImagePath('プレスリリース', selectedCategory === 'プレスリリース')}
                        alt="プレスリリース"
                        className="w-24 h-7 mx-auto"
                      />
                    </button>
                    <button
                      onClick={() => setSelectedCategory('イベント')}
                      className="transition-opacity hover:opacity-80"
                    >
                      <img 
                        src={getCategoryImagePath('イベント', selectedCategory === 'イベント')}
                        alt="イベント"
                        className="w-24 h-7 mx-auto"
                      />
                    </button>
                  </div>
                </div>
                
                {/* PC版：従来通り */}
                <div className="hidden md:flex md:flex-wrap md:justify-center md:gap-4">
                  {categories.map((category) => (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className="transition-opacity hover:opacity-80"
                    >
                      <img 
                        src={getCategoryImagePath(category, selectedCategory === category)}
                        alt={category === 'all' ? 'すべて' : category}
                        className="w-35 h-10"
                      />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* ニュース一覧セクション */}
          <section className="tateishi-section">
            <div className="-mx-4 md:px-6">
              
              {loading ? (
                // ローディング表示
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mb-4"></div>
                  <p className="body-text" style={{color: '#8b4513', opacity: 0.8}}>
                    ニュースを読み込んでいます...
                  </p>
                </div>
              ) : filteredNews.length === 0 ? (
                // データなし表示
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl">📰</span>
                  </div>
                  <p className="heading-3 mb-2" style={{color: '#8b4513'}}>
                    {selectedCategory === 'all' ? 'ニュースがありません' : `${selectedCategory}のニュースがありません`}
                  </p>
                  <p className="body-text" style={{color: '#8b4513', opacity: 0.6}}>
                    しばらくしてから再度ご確認ください
                  </p>
                </div>
              ) : (
                // ニュース一覧表示
                <div className="space-y-6">
                  {filteredNews.map((news, index) => {
                    const categoryValue = Array.isArray(news.category) ? news.category[0] : news.category;
                    
                    return (
                      <article 
                        key={news.id} 
                        className="relative rounded-xl md:rounded-2xl shadow-sm border border-stone-200 overflow-hidden hover:shadow-md transition-shadow"
                        style={{
                          backgroundColor: getCategoryBgColor(news.category)
                        }}
                      >
                        {/* スマホ版外部リンクボタン */}
                        {news.external_link && (
                          <div className="md:hidden absolute top-6 right-6 z-10">
                            <a 
                              href={news.external_link} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-lg text-xs font-bold transition-colors"
                            >
                              リンク
                            </a>
                          </div>
                        )}

                        {/* スマホ版：カード背景の大きな絵文字 */}
                        <div className="md:hidden absolute inset-0 flex items-center justify-center opacity-10 pointer-events-none">
                          <span className="text-8xl">
                            {categoryValue === 'お知らせ' ? '📢' : 
                             categoryValue === '受賞' ? '🏆' : 
                             categoryValue === 'プレスリリース' ? '📺' : 
                             categoryValue === 'イベント' ? '📅' : '📢'}
                          </span>
                        </div>

                        <div className="p-6 md:p-8 relative">
                          <div className="grid grid-cols-1 md:grid-cols-[100px_1fr_100px] gap-4 items-start">
                            
                            {/* 左側：カテゴリと日付（スマホは中央、PCは100px固定幅） */}
                            <div className="flex flex-col md:flex-col items-center text-center order-1 md:order-1">
                              <img 
                                src={getCategoryImagePath(categoryValue, true)}
                                alt={categoryValue}
                                className="mb-2"
                                style={{height: '24px', width: 'auto'}}
                              />
                              <p className="caption-text" style={{color: '#8b4513', opacity: 0.7}}>
                                {formatDate(news.date)}
                              </p>
                            </div>

                            {/* 中央：タイトルと内容（スマホは2番目、PCは中央寄せ） */}
                            <div className="text-center order-2 md:order-2">
                              <h3 className="heading-3 mb-3 md:mb-2" style={{color: '#8b4513'}}>
                                {news.title}
                              </h3>
                              {news.content && (
                                <div 
                                  className="body-text leading-relaxed prose prose-sm max-w-none text-center" 
                                  style={{color: '#8b4513', opacity: 0.8}}
                                  dangerouslySetInnerHTML={{__html: news.content}}
                                />
                              )}
                            </div>

                            {/* 右側：絵文字配置（スマホは非表示、PCは100px固定幅） */}
                            <div className="hidden md:flex justify-center items-end pt-4 order-3 relative">
                              {/* 外部リンクボタン */}
                              {news.external_link && (
                                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 z-10" style={{opacity: 1, width: '100px'}}>
                                  <a 
                                    href={news.external_link} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-lg text-xs font-bold transition-colors block text-center"
                                    style={{width: '100%'}}
                                  >
                                    リンク
                                  </a>
                                </div>
                              )}
                              <span className="text-4xl opacity-20 mt-4">
                                {categoryValue === 'お知らせ' ? '📢' : 
                                 categoryValue === '受賞' ? '🏆' : 
                                 categoryValue === 'プレスリリース' ? '📺' : 
                                 categoryValue === 'イベント' ? '📅' : '📢'}
                              </span>
                            </div>

                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </div>
          </section>

        </div>
      </div>

      {/* SVG区切り2 */}
      <SectionDivider title="awards" />

      <div className="tateishi-content ml-0 md:ml-40">
        <div className="mx-auto">

          {/* 主な受賞歴セクション */}
          <section className="tateishi-section">
            <div className="-mx-4 md:px-6">
              <div className="text-center mb-8 md:mb-12">
                <h2 className="heading-1 mb-4" style={{color: '#8b4513'}}>
                  主な受賞歴
                </h2>
                <p className="heading-3" style={{color: '#8b4513', opacity: 0.8}}>
                  科学的農業の成果が認められ、数々の受賞をいただいています
                </p>
              </div>
              
              {achievements.length > 0 ? (
                <div className="flex flex-wrap justify-center gap-6">
                  {achievements.map((achievement, index) => {
                    const date = new Date(achievement.date);
                    const formattedDate = `${date.getFullYear()}年${date.getMonth() + 1}月`;
                    
                    // 背景画像を決定
                    const backgroundImage = getBackgroundImage(achievement.title);
                    
                    return (
                      <div 
                        key={achievement.id} 
                        className="relative text-center"
                        style={{
                          width: '400px',
                          height: '200px',
                          borderRadius: '12px',
                          backgroundImage: `url(${backgroundImage})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          backgroundRepeat: 'no-repeat'
                        }}
                      >
                        <div className="p-4 flex flex-col justify-center h-full text-center">
                          <p className="caption-text mb-2" style={{color: '#8b4513'}}>{formattedDate}</p>
                          <h4 className="heading-3" style={{color: '#8b4513'}}>{achievement.title}</h4>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex justify-center">
                  <div 
                    className="flex items-center justify-center"
                    style={{
                      width: '400px',
                      height: '200px',
                      borderRadius: '12px',
                      backgroundColor: '#f3f4f6',
                      border: '2px dashed #d1d5db'
                    }}
                  >
                    <div className="text-center">
                      <p className="heading-3 mb-2" style={{color: '#8b4513'}}>実績データを読み込めませんでした</p>
                      <p className="caption-text" style={{color: '#8b4513', opacity: 0.6}}>しばらくしてから再度お試しください</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>

        </div>
      </div>

      {/* SVG区切り3 */}
      <SectionDivider title="future" />

      <div className="tateishi-content ml-0 md:ml-40">
        <div className="max-w-4xl mx-auto">

          {/* 将来の目標セクション */}
          <section className="tateishi-section">
            <div className="-mx-4 md:px-6">
              <div className="text-center mb-8 md:mb-12">
                <p className="heading-3" style={{color: '#8b4513', opacity: 0.8}}>
                  <span style={{color: '#ff8c00'}}>持続可能</span>な農業と<span style={{color: '#ff8c00'}}>地域発展</span>に向けた取り組み
                </p>
              </div>
              
              <div className="responsive-grid-2">
                {/* 短期目標 */}
                <div className="bg-gradient-to-br from-yellow-50 to-orange-100 rounded-xl p-4 md:p-6 border border-yellow-200">
                  <div className="text-center mb-3 md:mb-4">
                    <h3 className="heading-3" style={{color: '#8b4513'}}>短期目標</h3>
                    <p className="caption-text" style={{color: '#8b4513', opacity: 0.8}}>2025-2026年</p>
                  </div>
                  <div className="space-y-2 text-center">
                    <p className="caption-text leading-relaxed" style={{color: '#8b4513', opacity: 0.8}}>
                      • 佐賀県特別栽培基準の取得
                    </p>
                    <p className="caption-text leading-relaxed" style={{color: '#8b4513', opacity: 0.8}}>
                      • さつまいも販売先の拡大
                    </p>
                    <p className="caption-text leading-relaxed" style={{color: '#8b4513', opacity: 0.8}}>
                      • 農園見学体制の充実
                    </p>
                  </div>
                </div>

                {/* 長期目標 */}
                <div className="bg-gradient-to-br from-green-50 to-blue-100 rounded-xl p-4 md:p-6 border border-green-200">
                  <div className="text-center mb-3 md:mb-4">
                    <h3 className="heading-3" style={{color: '#8b4513'}}>長期目標</h3>
                    <p className="caption-text" style={{color: '#8b4513', opacity: 0.8}}>2027年以降</p>
                  </div>
                  <div className="space-y-2 text-center">
                    <p className="caption-text leading-relaxed" style={{color: '#8b4513', opacity: 0.8}}>
                      • 循環型農業のモデル確立
                    </p>
                    <p className="caption-text leading-relaxed" style={{color: '#8b4513', opacity: 0.8}}>
                      • 次世代農業者の育成支援
                    </p>
                    <p className="caption-text leading-relaxed" style={{color: '#8b4513', opacity: 0.8}}>
                      • 地域農業活性化への貢献
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

        </div>
      </div>

      {/* お問い合わせボタン（枠外） */}
      <div className="tateishi-content ml-0 md:ml-40">
        <div className="max-w-4xl mx-auto mb-12">
          <div className="text-center">
            <a
              href="/contact"
              className="inline-block px-6 md:px-8 py-3 md:py-4 text-base md:text-lg font-medium rounded-lg transition-all duration-300"
              style={{
                color: '#fff',
                backgroundColor: '#8b4513',
                border: '2px solid #8b4513',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(139, 69, 19, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              お問い合わせはこちら
            </a>
          </div>
        </div>
      </div>

      {/* SVG区切り4 */}
      <SectionDivider title="instagram" />

      <div className="tateishi-content ml-0 md:ml-40">
        <div className="max-w-4xl mx-auto">

          {/* Instagram投稿セクション */}
          <section className="tateishi-section">
            <div className="-mx-4 md:px-6">
              <div className="text-center mb-8 md:mb-12">
                <p className="heading-3" style={{color: '#8b4513', opacity: 0.8}}>
                  <span style={{color: '#ff8c00'}}>日々</span>の農園の様子をお伝えします
                </p>
              </div>
              
              {instagramPosts.length === 0 ? (
                // データなし表示
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl">📸</span>
                  </div>
                  <p className="heading-3 mb-2" style={{color: '#8b4513'}}>
                    Instagram投稿がありません
                  </p>
                  <p className="body-text" style={{color: '#8b4513', opacity: 0.6}}>
                    しばらくしてから再度ご確認ください
                  </p>
                </div>
              ) : (
                <div>
                  {/* PC版：3列グリッドレイアウト */}
                  <div className="hidden md:grid md:grid-cols-3 gap-6">
                    {instagramPosts.map((post, index) => (
                      <a
                        key={post.id}
                        href="https://www.instagram.com/08._.tate/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-white rounded-xl shadow-md overflow-hidden border border-stone-100 hover:shadow-lg transition-shadow block"
                      >
                        <div className="aspect-square overflow-hidden">
                          <img 
                            src={post.images?.[0]?.url || '/images/placeholder-instagram.jpg'} 
                            alt={`Instagram投稿${index + 1}`}
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                      </a>
                    ))}
                  </div>

                  {/* スマホ版：横スクロール */}
                  <div className="md:hidden overflow-x-auto">
                    <div className="flex space-x-4 pb-4" style={{width: 'max-content'}}>
                      {instagramPosts.map((post, index) => (
                        <a
                          key={post.id}
                          href="https://www.instagram.com/08._.tate/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-white rounded-xl shadow-md overflow-hidden border border-stone-100 flex-shrink-0 block"
                          style={{width: '280px'}}
                        >
                          <div className="aspect-square overflow-hidden">
                            <img 
                              src={post.images?.[0]?.url || '/images/placeholder-instagram.jpg'} 
                              alt={`Instagram投稿${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>

        </div>
      </div>

      {/* Instagramへのリンク（枠外） */}
      <div className="tateishi-content ml-0 md:ml-40">
        <div className="max-w-4xl mx-auto mb-12">
          <div className="text-center">
            <a
              href="https://www.instagram.com/08._.tate/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block text-white px-6 md:px-8 py-3 md:py-4 text-base md:text-lg font-medium rounded-lg transition-all duration-300 hover:opacity-90"
              style={{background: 'linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)'}}
            >
              Instagramをもっと見る →
            </a>
          </div>
        </div>
      </div>

    </div>
  );
}